import { isURL } from 'validator';
import { type ZodType, z } from 'zod';

function convertIDToString(id: unknown): string {
  if (typeof id === 'string') {
    return id;
  }

  if (typeof id === 'object' && id?.toString != null && typeof id.toString === 'function') {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    return (id as any).toString();
  }

  // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
  throw new Error(`id ${id} is invalid`);
}

// REF: we don't want to use any dependencies besides zod
//      this will be copied to the frontend side and we want to use it
function isValidMongoId(id: unknown) {
  return /^[a-fA-F0-9]{24}$/.test(convertIDToString(id));
}

export const mongoIdSchema = z.string().refine(isValidMongoId, {
  message: 'Invalid MongoDB ObjectId',
});

export const clerkProviderAuthIdSchema = z.string().regex(/user_[a-zA-Z0-9]{20,40}$/, {
  message: 'Invalid Clerk User ID',
});

export function defaultNullish<T extends ZodType>(schema: T) {
  return z.preprocess((arg) => (arg == null ? undefined : arg), schema);
}

function isNumericString(str: string) {
  return /^[0-9]+$/.test(str);
}

export function numericStringSchema(min: number = 0, max: number = 99999) {
  return z.string().min(min).max(max).refine(isNumericString, {
    message: 'Invalid numeric string',
  });
}

// Common valid TLDs (most used ones)
const VALID_TLDS = new Set([
  // Generic TLDs
  'com',
  'org',
  'net',
  'edu',
  'gov',
  'mil',
  'int',
  'info',
  'biz',
  'name',
  'pro',
  'aero',
  'coop',
  'museum',
  'app',
  'dev',
  'io',
  'co',
  'ai',
  'me',
  'tv',
  'cc',
  'ws',
  'blog',
  'shop',
  'store',
  'online',
  'site',
  'website',
  'tech',
  'cloud',
  'digital',
  'agency',
  'studio',
  'design',
  'marketing',
  'media',
  'news',
  'email',
  'support',
  'solutions',
  'services',
  'consulting',
  'software',
  'systems',
  'network',
  'global',
  'world',
  'live',
  'life',
  'work',
  'jobs',
  'careers',
  'education',
  'training',
  'academy',
  'school',
  'university',
  'institute',
  'health',
  'medical',
  'clinic',
  'hospital',
  'dental',
  'fitness',
  'yoga',
  'travel',
  'tours',
  'hotel',
  'flights',
  'booking',
  'vacation',
  'holiday',
  'food',
  'restaurant',
  'cafe',
  'bar',
  'pizza',
  'coffee',
  'fashion',
  'style',
  'beauty',
  'shoes',
  'clothing',
  'photo',
  'photography',
  'video',
  'film',
  'music',
  'art',
  'gallery',
  'game',
  'games',
  'play',
  'fun',
  'cool',
  'lol',
  'wtf',
  'money',
  'finance',
  'bank',
  'insurance',
  'invest',
  'capital',
  'fund',
  'cash',
  'credit',
  'loan',
  'legal',
  'law',
  'lawyer',
  'attorney',
  'realty',
  'estate',
  'property',
  'house',
  'home',
  'land',
  'rent',
  'auto',
  'car',
  'cars',
  'bike',
  'taxi',
  'social',
  'chat',
  'dating',
  'meet',
  'party',
  'events',
  'club',
  'community',
  'forum',
  'group',
  'team',
  'plus',
  'one',
  'zone',
  'space',
  'page',
  'link',
  'click',
  'today',
  'now',
  'top',
  'best',
  'vip',
  'pro',
  'xyz',
  'icu',
  'cyou',
  'bond',
  'cfd',
  'sbs',
  'monster',
  'quest',
  // Country code TLDs (ccTLDs)
  'ac',
  'ad',
  'ae',
  'af',
  'ag',
  'al',
  'am',
  'ao',
  'aq',
  'ar',
  'as',
  'at',
  'au',
  'aw',
  'ax',
  'az',
  'ba',
  'bb',
  'bd',
  'be',
  'bf',
  'bg',
  'bh',
  'bi',
  'bj',
  'bm',
  'bn',
  'bo',
  'br',
  'bs',
  'bt',
  'bw',
  'by',
  'bz',
  'ca',
  'cd',
  'cf',
  'cg',
  'ch',
  'ci',
  'ck',
  'cl',
  'cm',
  'cn',
  'cr',
  'cu',
  'cv',
  'cw',
  'cx',
  'cy',
  'cz',
  'de',
  'dj',
  'dk',
  'dm',
  'do',
  'dz',
  'ec',
  'ee',
  'eg',
  'er',
  'es',
  'et',
  'eu',
  'fi',
  'fj',
  'fk',
  'fm',
  'fo',
  'fr',
  'ga',
  'gb',
  'gd',
  'ge',
  'gf',
  'gg',
  'gh',
  'gi',
  'gl',
  'gm',
  'gn',
  'gp',
  'gq',
  'gr',
  'gt',
  'gu',
  'gw',
  'gy',
  'hk',
  'hm',
  'hn',
  'hr',
  'ht',
  'hu',
  'id',
  'ie',
  'il',
  'im',
  'in',
  'iq',
  'ir',
  'is',
  'it',
  'je',
  'jm',
  'jo',
  'jp',
  'ke',
  'kg',
  'kh',
  'ki',
  'km',
  'kn',
  'kp',
  'kr',
  'kw',
  'ky',
  'kz',
  'la',
  'lb',
  'lc',
  'li',
  'lk',
  'lr',
  'ls',
  'lt',
  'lu',
  'lv',
  'ly',
  'ma',
  'mc',
  'md',
  'mg',
  'mh',
  'mk',
  'ml',
  'mm',
  'mn',
  'mo',
  'mp',
  'mq',
  'mr',
  'ms',
  'mt',
  'mu',
  'mv',
  'mw',
  'mx',
  'my',
  'mz',
  'na',
  'nc',
  'ne',
  'nf',
  'ng',
  'ni',
  'nl',
  'no',
  'np',
  'nr',
  'nu',
  'nz',
  'om',
  'pa',
  'pe',
  'pf',
  'pg',
  'ph',
  'pk',
  'pl',
  'pm',
  'pn',
  'pr',
  'ps',
  'pt',
  'pw',
  'py',
  'qa',
  're',
  'ro',
  'rs',
  'ru',
  'rw',
  'sa',
  'sb',
  'sc',
  'sd',
  'se',
  'sg',
  'sh',
  'si',
  'sk',
  'sl',
  'sm',
  'sn',
  'so',
  'sr',
  'ss',
  'st',
  'sv',
  'sx',
  'sy',
  'sz',
  'tc',
  'td',
  'tf',
  'tg',
  'th',
  'tj',
  'tk',
  'tl',
  'tm',
  'tn',
  'to',
  'tr',
  'tt',
  'tw',
  'tz',
  'ua',
  'ug',
  'uk',
  'us',
  'uy',
  'uz',
  'va',
  'vc',
  've',
  'vg',
  'vi',
  'vn',
  'vu',
  'wf',
  'ye',
  'yt',
  'za',
  'zm',
  'zw',
]);

function isValidTLD(value: string) {
  const url = new URL(value);
  const hostname = url.hostname;
  const parts = hostname.split('.');
  const tld = parts[parts.length - 1].toLowerCase();
  return VALID_TLDS.has(tld);
}

export const urlSchema = z.string().refine(
  function (value) {
    // eslint-disable-next-line camelcase
    if (!isURL(value, { require_protocol: true })) {
      return false;
    }
    // Validate hostname ends with a valid TLD
    try {
      return isValidTLD(value);
    } catch {
      return false;
    }
  },
  {
    message: 'URL_INVALID',
  }
);

export const urlSchemaOptional = z
  .string()
  .optional()
  .refine(
    function (value) {
      if (value == null || value === '') {
        return true;
      }
      // eslint-disable-next-line camelcase
      if (!isURL(value, { require_protocol: true })) {
        return false;
      }
      // Validate hostname ends with a valid TLD
      try {
        return isValidTLD(value);
      } catch {
        return false;
      }
    },
    {
      message: 'URL_INVALID',
    }
  );

export const urlArraySchemaOptional = z.array(urlSchemaOptional);
